import React, { useState, FormEvent, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useAddFeature } from 'services/layer'
import { FeatureMetaParams, Layer } from 'models/layer'
import { Dialog } from 'components/Dialog'
import { Stack } from 'components/Stack'
import { Flex } from 'components/Flex'
import { Heading } from 'components/Heading'
import { TextInput } from 'components/TextInput'
import { SelectInput } from 'components/SelectInput'
import { buildSimpleOptions } from 'utils/selectOption'
import { Button } from 'components/Button'
import { Text } from 'components/Text'
import { TextArea } from 'components/TextArea'
import { getIcon } from '../../../utils'
import { useMapEditorContext } from 'context/map-editor-context'
import { fire } from 'utils/customEvent'
import { Events, RefreshLayer } from 'constants/events'
import { AreaSelection } from 'models/editor'
import { Feature } from 'models/map'
import styles from './AddFeatureDialog.module.scss'

type AddfeatureDialogProps = {
  feature: Feature
  areaTool: AreaSelection
  layer: Layer
}

export const AddFeatureDialog: FC<AddfeatureDialogProps> = ({ layer, feature, areaTool }) => {
  const { t } = useTranslation()
  const mapEditorDispatch = useMapEditorContext()[1]

  const [form, setForm] = useState<FeatureMetaParams>(
    layer.fields!.reduce((object, field) => ({ ...object, [field.key]: '' }), {}),
  )
  const [addFeature] = useAddFeature(layer.id)

  const onClose = () => mapEditorDispatch.setConfirm(false)

  const onFormSubmit = (event: FormEvent) => {
    event.preventDefault()

    return addFeature(
      { feature, data: form },
      {
        onSuccess: () => {
          mapEditorDispatch.featureCreated()
          fire<RefreshLayer>(Events.RefreshLayer, { sourceLayer: layer.viz.source! })
        },
      },
    )
  }

  return (
    <Dialog
      aria-label={t(`layer_update_dialog.${layer?.geometryType}.title`)}
      isOpen={true}
      onClose={onClose}
    >
      <div className={styles.layerUpdateDialog}>
        <Stack>
          <header>
            <Flex justify="start" align="center">
              <span className={styles.icon}>{getIcon(areaTool)}</span>
              <Heading size="card">{t(`layer_update_dialog.${layer.geometryType}.title`)}</Heading>
            </Flex>
            <Text size="small" component="span">
              {t(`layer_update_dialog.${layer.geometryType}.desc`)}
            </Text>
          </header>

          <form onSubmit={onFormSubmit}>
            <Stack space="medium">
              {layer.fields?.map((field) => {
                switch (field.type) {
                  case 'text':
                    return (
                      <TextInput
                        key={field.key}
                        name={field.key}
                        label={field.name}
                        onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                        value={form[field.key]}
                      />
                    )
                  case 'textarea':
                    return (
                      <TextArea
                        key={field.key}
                        name={field.key}
                        label={field.name}
                        onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                        value={form[field.key]}
                      />
                    )
                  case 'select':
                    return (
                      <SelectInput
                        key={field.key}
                        label={field.name}
                        options={buildSimpleOptions(field.options!)}
                        onChange={(value) =>
                          value ? setForm({ ...form, [field.key]: value.value }) : null
                        }
                      />
                    )
                }
              })}

              <Button type="submit" full>
                {t(`layer_update_dialog.${layer.geometryType}.cta`)}
              </Button>
            </Stack>
          </form>
        </Stack>
      </div>
    </Dialog>
  )
}
